import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect } from "react";

export const Route = createFileRoute("/your-wardrobe")({
  head: () => ({
    meta: [
      { title: "Daily Outfit Suggestions From Your Own Wardrobe | Like2Fit" },
      {
        name: "description",
        content:
          "Like2Fit picks an outfit from your existing wardrobe every evening and emails it to you at 9pm. No shopping needed. No decision fatigue. Just get dressed.",
      },
      { property: "og:title", content: "Daily Outfit Suggestions From Your Own Wardrobe | Like2Fit" },
      {
        property: "og:description",
        content:
          "Like2Fit picks an outfit from your existing wardrobe every evening and emails it to you at 9pm. No shopping needed. No decision fatigue. Just get dressed.",
      },
      { property: "og:image", content: "/icons/icon-512.png" },
      { property: "og:url", content: "https://like2fit.com/your-wardrobe" },
      { property: "og:type", content: "website" },
    ],
    links: [{ rel: "canonical", href: "https://like2fit.com/your-wardrobe" }],
  }),
  component: YourWardrobeCampaign,
});

const INK = "#0A0A0A";
const CORAL = "#FF5C5C";
const TINT = "#FAECE7";
const MUTED = "rgba(10,10,10,0.62)";
const SERIF = "'Instrument Serif', 'Times New Roman', Georgia, serif";
const SANS = "'Work Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif";

function preserveUtmAndGo(campaign: "get-the-look" | "wardrobe") {
  try {
    const params = new URLSearchParams(window.location.search);
    sessionStorage.setItem("l2f_utm_source", params.get("utm_source") ?? "");
    sessionStorage.setItem("l2f_utm_medium", params.get("utm_medium") ?? "");
    sessionStorage.setItem("l2f_utm_campaign", params.get("utm_campaign") ?? "");
    sessionStorage.setItem("l2f_campaign_entry", campaign);
  } catch {
    /* ignore storage errors */
  }
  window.location.href = "/";
}

function YourWardrobeCampaign() {
  useEffect(() => {
    const id = "l2f-campaign-fonts";
    if (document.getElementById(id)) return;
    const link = document.createElement("link");
    link.id = id;
    link.rel = "stylesheet";
    link.href =
      "https://fonts.googleapis.com/css2?family=Instrument+Serif:ital@0;1&family=Work+Sans:wght@400;500;600;700&display=swap";
    document.head.appendChild(link);
  }, []);

  const onCTA = () => preserveUtmAndGo("wardrobe");

  return (
    <div style={{ minHeight: "100vh", background: "#fff", color: INK, fontFamily: SANS }}>
      {/* Header */}
      <header
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "20px 24px",
          borderBottom: "0.5px solid rgba(10,10,10,0.05)",
        }}
      >
        <span style={{ color: CORAL, fontWeight: 600, fontSize: 20, letterSpacing: "-0.5px" }}>
          Like2Fit
        </span>
        <Link
          to="/"
          style={{
            fontSize: 13,
            color: "rgba(10,10,10,0.7)",
            textDecoration: "none",
            fontWeight: 500,
          }}
        >
          Sign in
        </Link>
      </header>

      <main style={{ maxWidth: 560, margin: "0 auto", padding: "0 24px" }}>
        {/* Hero */}
        <section style={{ padding: "56px 0 48px" }}>
          <p style={eyebrow}>Like2Fit · Your Wardrobe</p>
          <h1
            style={{
              fontFamily: SERIF,
              fontStyle: "italic",
              fontSize: 48,
              lineHeight: 1.05,
              letterSpacing: "-0.5px",
              margin: 0,
              color: INK,
            }}
          >
            Know what you're wearing tomorrow before you go to sleep.
          </h1>
          <p style={{ marginTop: 22, fontSize: 16, lineHeight: 1.6, color: MUTED }}>
            Like2Fit picks an outfit from your existing wardrobe every evening and sends it to you
            at 9pm. No shopping. No decision fatigue. Just get dressed.
          </p>

          <button onClick={onCTA} style={primaryBtn}>
            Set up my wardrobe — it's free
          </button>
          <p style={disclaimer}>Free forever · Takes 5 minutes to set up</p>
        </section>

        {/* Problem */}
        <section style={{ padding: "32px 0 56px" }}>
          <p style={eyebrow}>The 7am scramble</p>
          <h2 style={sectionHeading}>Sound familiar?</h2>

          <div style={{ display: "flex", flexDirection: "column", gap: 16, marginTop: 28 }}>
            <PainCard
              text="You spend 15 minutes every morning staring at your wardrobe and still leave feeling wrong."
            />
            <PainCard text="You own loads of clothes but wear the same 5 things on rotation." />
            <PainCard
              text="You know you have things that go together — you just can't think of them under pressure."
            />
          </div>
        </section>

        {/* Solution */}
        <section style={{ padding: "16px 0 56px" }}>
          <p style={eyebrow}>The fix</p>
          <h2 style={sectionHeading}>Like2Fit thinks for you.</h2>

          <div style={{ display: "flex", flexDirection: "column", gap: 28, marginTop: 32 }}>
            <Step
              n="01"
              icon="📷"
              title="Add your clothes"
              body="Photo your wardrobe once. Like2Fit remembers everything you own."
            />
            <Step
              n="02"
              icon="🌙"
              title="Get your look at 9pm"
              body="Every evening we pick an outfit from your wardrobe for tomorrow. Based on the occasion, weather and your mood."
            />
            <Step
              n="03"
              icon="☕"
              title="Just get dressed"
              body="Open the email, see your outfit, get dressed. That's it. No decisions. No stress."
            />
          </div>
        </section>

        {/* Daily email */}
        <section
          style={{
            background: TINT,
            borderRadius: 24,
            padding: "40px 28px",
            marginBottom: 56,
          }}
        >
          {/* Visual email mock */}
          <div
            style={{
              background: "#fff",
              borderRadius: 18,
              padding: "20px 18px",
              marginBottom: 28,
              boxShadow: "0 10px 30px -12px rgba(10,10,10,0.18)",
            }}
            aria-hidden="true"
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
                marginBottom: 12,
                fontSize: 11,
                color: "rgba(10,10,10,0.5)",
                fontWeight: 600,
                letterSpacing: "0.05em",
              }}
            >
              <span
                style={{
                  width: 8,
                  height: 8,
                  borderRadius: 999,
                  background: CORAL,
                  display: "inline-block",
                }}
              />
              LIKE2FIT · 21:00
            </div>
            <p
              style={{
                fontFamily: SERIF,
                fontStyle: "italic",
                fontSize: 22,
                margin: "0 0 6px",
                color: INK,
                lineHeight: 1.2,
              }}
            >
              Tomorrow you're wearing…
            </p>
            <p style={{ fontSize: 13, color: MUTED, margin: 0 }}>
              Cream knit · Wide-leg denim · White sneakers — picked for tomorrow's 14°C.
            </p>
          </div>

          <h2
            style={{
              fontFamily: SERIF,
              fontStyle: "italic",
              fontSize: 36,
              lineHeight: 1.1,
              letterSpacing: "-0.3px",
              margin: "0 0 16px",
              color: INK,
            }}
          >
            Tomorrow's outfit. Delivered at 9pm.
          </h2>
          <p
            style={{
              fontSize: 16,
              lineHeight: 1.6,
              color: "rgba(10,10,10,0.75)",
              margin: "0 0 20px",
            }}
          >
            Every evening Like2Fit sends you a personalised outfit from your own wardrobe — no
            shopping required. Just open, read, and know exactly what to wear.
          </p>
          <ul style={{ listStyle: "none", padding: 0, margin: 0, display: "grid", gap: 10 }}>
            {[
              "Based on tomorrow's weather",
              "Matched to your occasion",
              "Items you actually own",
              "Free to enable",
            ].map((t) => (
              <li key={t} style={{ display: "flex", gap: 10, fontSize: 14, color: INK }}>
                <span style={{ color: CORAL, fontWeight: 700 }}>✓</span>
                {t}
              </li>
            ))}
          </ul>
        </section>

        {/* Wardrobe insights */}
        <section style={{ padding: "0 0 64px" }}>
          <p style={eyebrow}>The hidden bonus</p>
          <h2 style={sectionHeading}>Your wardrobe. Finally understood.</h2>
          <div style={{ display: "flex", flexDirection: "column", gap: 14, marginTop: 24 }}>
            {[
              "See which items you actually wear",
              "Discover forgotten combinations",
              "Know what's missing from your wardrobe",
            ].map((t) => (
              <div
                key={t}
                style={{
                  display: "flex",
                  gap: 12,
                  alignItems: "center",
                  padding: "14px 18px",
                  border: "0.5px solid rgba(10,10,10,0.08)",
                  borderRadius: 14,
                  fontSize: 15,
                  color: INK,
                }}
              >
                <span style={{ color: CORAL, fontWeight: 700 }}>→</span>
                {t}
              </div>
            ))}
          </div>
        </section>

        {/* Final CTA */}
        <section
          style={{
            background: INK,
            color: "#fff",
            borderRadius: 24,
            padding: "56px 28px",
            textAlign: "center",
            margin: "0 0 56px",
          }}
        >
          <p
            style={{
              fontFamily: SERIF,
              fontStyle: "italic",
              fontSize: 34,
              lineHeight: 1.1,
              margin: "0 0 28px",
            }}
          >
            Your mornings sorted. Starting tonight.
          </p>
          <button onClick={onCTA} style={{ ...primaryBtn, marginTop: 0 }}>
            Set up my wardrobe — it's free
          </button>
          <p style={{ ...disclaimer, color: "rgba(255,255,255,0.6)" }}>
            Free forever · No credit card · Takes 5 minutes
          </p>
        </section>
      </main>

      {/* Footer */}
      <footer
        style={{
          padding: "32px 24px 48px",
          borderTop: "0.5px solid rgba(10,10,10,0.06)",
          textAlign: "center",
        }}
      >
        <p style={{ color: CORAL, fontWeight: 600, fontSize: 18, margin: "0 0 12px" }}>Like2Fit</p>
        <nav
          aria-label="Footer"
          style={{ display: "flex", gap: 18, justifyContent: "center", fontSize: 13 }}
        >
          <Link to="/privacy" style={footerLink}>
            Privacy
          </Link>
          <Link to="/terms" style={footerLink}>
            Terms
          </Link>
          <Link to="/" style={footerLink}>
            Back to home
          </Link>
        </nav>
      </footer>
    </div>
  );
}

function Step({ n, icon, title, body }: { n: string; icon: string; title: string; body: string }) {
  return (
    <div style={{ display: "flex", gap: 18, alignItems: "flex-start" }}>
      <div
        style={{
          flexShrink: 0,
          width: 56,
          height: 56,
          borderRadius: 16,
          background: TINT,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: 24,
        }}
        aria-hidden="true"
      >
        {icon}
      </div>
      <div style={{ flex: 1 }}>
        <p
          style={{
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: "0.2em",
            color: CORAL,
            margin: "0 0 6px",
          }}
        >
          {n}
        </p>
        <h3 style={{ fontSize: 19, fontWeight: 700, margin: "0 0 6px", color: INK }}>{title}</h3>
        <p style={{ fontSize: 15, lineHeight: 1.55, color: MUTED, margin: 0 }}>{body}</p>
      </div>
    </div>
  );
}

function PainCard({ text }: { text: string }) {
  return (
    <div
      style={{
        padding: "18px 20px",
        border: "0.5px solid rgba(10,10,10,0.08)",
        borderRadius: 16,
        background: "#fff",
        fontSize: 15,
        lineHeight: 1.55,
        color: "rgba(10,10,10,0.82)",
        fontStyle: "italic",
        fontFamily: SERIF,
      }}
    >
      "{text}"
    </div>
  );
}

const primaryBtn: React.CSSProperties = {
  display: "block",
  width: "100%",
  marginTop: 32,
  padding: "18px 24px",
  background: CORAL,
  color: "#fff",
  border: "none",
  borderRadius: 999,
  fontSize: 16,
  fontWeight: 600,
  cursor: "pointer",
  fontFamily: "inherit",
  letterSpacing: "-0.1px",
};

const disclaimer: React.CSSProperties = {
  marginTop: 14,
  fontSize: 13,
  color: "rgba(10,10,10,0.5)",
  textAlign: "center",
};

const eyebrow: React.CSSProperties = {
  fontSize: 11,
  fontWeight: 700,
  textTransform: "uppercase",
  letterSpacing: "0.25em",
  color: CORAL,
  margin: "0 0 20px",
};

const sectionHeading: React.CSSProperties = {
  fontFamily: SERIF,
  fontStyle: "italic",
  fontSize: 36,
  lineHeight: 1.1,
  letterSpacing: "-0.3px",
  margin: 0,
  color: INK,
};

const footerLink: React.CSSProperties = {
  color: "rgba(10,10,10,0.7)",
  textDecoration: "none",
};
